import { Component, type ReactNode, type ErrorInfo } from 'react'
import { trackException } from '@/services/monitoringService'
import { getMessage } from '@/utils/messages'

interface Props {
  children: ReactNode
  /** Optional custom fallback UI */
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * React class-based Error Boundary.
 * Catches render-time exceptions, reports them to Application Insights,
 * and renders a friendly fallback UI with a reset option.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    trackException(error, {
      componentStack: info.componentStack ?? '',
      location: window.location.pathname,
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex flex-col items-center justify-center min-h-[300px] p-8 text-center">
          <div className="text-[48px] mb-4">⚠️</div>
          <h2
            className="text-[18px] font-bold text-[var(--text)] mb-2"
            style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
          >
            {getMessage('error_boundary.title')}
          </h2>
          <p className="text-[12px] text-[var(--text-muted)] font-mono mb-2 max-w-sm">
            {this.state.error?.message ?? getMessage('api.error.unexpected')}
          </p>
          <p className="text-[11px] text-[var(--text-muted)] font-mono mb-6 max-w-sm">
            {getMessage('error_boundary.description')}
          </p>
          <button
            onClick={this.handleReset}
            className="px-4 py-2 rounded-[var(--radius-sm)] text-white font-mono text-[12px] bg-[var(--accent)] hover:opacity-90 transition-opacity"
          >
            {getMessage('error_boundary.button_reset')}
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
